import {
	AfterViewInit,
	Component,
	ElementRef,
	HostListener,
	Inject,
	OnDestroy,
	OnInit,
	Renderer2,
	viewChild,
	AfterContentInit,
} from '@angular/core';
import {
	AdressverwaltungFacade,
	KontaktInVerfahrenListResource,
	KontaktKategorie,
	KontaktKategorieLabel,
	KontaktResource,
	VerfahrenKontakt,
} from '@schir-int-client/adressverwaltung-shared';
import { Subject, Subscription } from 'rxjs';
import { VerfahrenFacade } from '@schir-int-client/verfahren-shared';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { isNil } from 'lodash-es';
import { filter } from 'rxjs/operators';
import { AktenzeichenRendererProvider, AktenzeichenUtil } from '@schir-int-client/aktenzeichen-shared';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { getIdNumber } from '../../../../../../../test-helpers/src/lib/resource-helper';

type DialogData = {
	kontakt: KontaktResource;
	kontakteInSelectedVerfahren: KontaktInVerfahrenListResource;
};

@Component({
	selector: 'schir-int-client-kontakt-assign-menu',
	templateUrl: './kontakt-assign-menu.component.html',
	styleUrls: ['./kontakt-assign-menu.component.scss'],
	standalone: false,
})
export class KontaktAssignMenuComponent implements OnDestroy, OnInit, AfterViewInit, AfterContentInit {
	readonly root = viewChild<ElementRef>('assignMenuRoot');

	selectedKontakt: KontaktResource;

	readonly kontaktKategorie = KontaktKategorie;
	readonly kontaktKategorieLabel = KontaktKategorieLabel;

	selectedVerfahrenAktenzeichen: string;
	selectedVerfahrenId: number;

	form: UntypedFormGroup;

	private subscriptions: Subscription[] = [];
	private inside: boolean;

	private readonly initialFormValue: string;
	audioAlert: Subject<string> = new Subject<string>();

	protected title = '';

	constructor(
		@Inject(MAT_DIALOG_DATA) data: DialogData,
		private formBuilder: UntypedFormBuilder,
		public adressverwaltungFacade: AdressverwaltungFacade,
		private verfahrenFacade: VerfahrenFacade,
		private rendererProvider: AktenzeichenRendererProvider,
		private renderer: Renderer2,
		private matDialogRef: MatDialogRef<KontaktAssignMenuComponent, any>
	) {
		this.selectedKontakt = data.kontakt;

		const selectedKontaktInVerfahren = (
			data.kontakteInSelectedVerfahren._embedded?.kontaktInVerfahrenResList ?? []
		).find(entry => entry.kontakt._links.self.href === this.selectedKontakt._links.self.href);
		const selectedKategorien = new Set(selectedKontaktInVerfahren?.inVerfahren?.kategorien ?? []);

		this.form = this.createForm(selectedKategorien);
		this.initialFormValue = JSON.stringify(this.form.getRawValue());
	}

	private createForm(selectedKategorien: Set<KontaktKategorie>) {
		return this.formBuilder.group(
			Object.values(KontaktKategorie).reduce(
				(controls, kategorie) => {
					controls[kategorie] = selectedKategorien.has(kategorie);
					return controls;
				},
				{} as Record<string, boolean>
			)
		);
	}

	getFormControl(controlName: string): UntypedFormControl {
		return <UntypedFormControl>this.form.controls[controlName];
	}

	ngOnInit() {
		this.subscriptions.push(
			this.verfahrenFacade.verfahrenSingle.pipe(filter(verfahren => !isNil(verfahren))).subscribe(verfahren => {
				const aktenzeichen = AktenzeichenUtil.parse(verfahren);
				this.selectedVerfahrenAktenzeichen = this.rendererProvider.getRenderer().render(aktenzeichen);
				this.selectedVerfahrenId = getIdNumber(verfahren);
			})
		);
		this.subscriptions.push(this.matDialogRef.beforeClosed().subscribe(() => this.dialogClosed()));
	}

	ngAfterViewInit() {
		// Höhenkorrektur, wenn notwendig:
		const nativeElement = this.root().nativeElement.parentElement.parentElement;
		const bottom = nativeElement.getBoundingClientRect().bottom;
		if (window.innerHeight < bottom + 10) {
			this.renderer.setStyle(nativeElement, 'bottom', '10px');
		}
	}

	ngAfterContentInit() {
		this.title = this.formatTitle();
	}

	ngOnDestroy() {
		this.subscriptions.forEach(s => s.unsubscribe());
	}

	@HostListener('click')
	clickedInside() {
		this.inside = true;
	}

	@HostListener('document:click')
	clickedOutside() {
		if (this.inside != null && !this.inside) {
			this.matDialogRef.close();
		}
		this.inside = false;
	}

	private dialogClosed(): void {
		const formValue = this.form.getRawValue();
		if (JSON.stringify(formValue) !== this.initialFormValue) {
			const kategorien = (Object.entries(formValue) as Array<[KontaktKategorie, boolean]>)
				.filter(([, selected]) => selected)
				.map(([kategorie]) => kategorie);
			const kontaktId = getIdNumber(this.selectedKontakt);
			const verfahrenKontakt: VerfahrenKontakt = { verfahrenId: this.selectedVerfahrenId, kontaktId, kategorien };
			this.adressverwaltungFacade.setKontaktAssignment(verfahrenKontakt);
			this.audioAlert.next('Änderungen in der Rollenzuordnung des Kontakts wurden gespeichert.');
		}
	}

	private formatTitle(): string {
		if (this.selectedKontakt.juristischePerson) {
			let title = this.selectedKontakt.firmenName;
			if (this.selectedKontakt.firmenName1) {
				title += ', ' + this.selectedKontakt.firmenName1;
			}
			if (this.selectedKontakt.firmenName2) {
				title += ', ' + this.selectedKontakt.firmenName2;
			}
			return title;
		} else {
			let title = '';
			if (this.selectedKontakt.vorname) {
				title += this.selectedKontakt.vorname + ' ';
			}
			return title + this.selectedKontakt.name;
		}
	}
}
